import { useEffect, useState } from 'react';
import { api2, ApiError, type IdeaDto } from '../api.ts';
import AttachField, { type AttachedFile } from '../components/AttachField.tsx';
import AutoTextarea from '../components/AutoTextarea.tsx';

/**
 * The shelf of things that worked somewhere, and the place to pitch your own.
 *
 * Two product decisions carried over from the prototype and worth keeping:
 * an idea you pitch is private until it is approved, and everything is
 * attributed to the city that ran it rather than to a person.
 */
export default function Ideas({ cityName }: { cityName: string | null }): React.ReactElement {
  const [mine, setMine] = useState<IdeaDto[]>([]);
  const [shared, setShared] = useState<IdeaDto[]>([]);
  const [pitching, setPitching] = useState(false);
  const [title, setTitle] = useState('');
  const [pitch, setPitch] = useState('');
  const [proposing, setProposing] = useState(false);
  const [evTitle, setEvTitle] = useState('');
  const [evDate, setEvDate] = useState('');
  const [evVenue, setEvVenue] = useState('');
  const [evCost, setEvCost] = useState('');
  const [evPitch, setEvPitch] = useState('');
  const [evFile, setEvFile] = useState<AttachedFile | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const apply = (res: { mine: IdeaDto[]; shared: IdeaDto[] }) => {
    setMine(res.mine);
    setShared(res.shared);
  };

  useEffect(() => {
    api2
      .ideas()
      .then(apply)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load ideas.'))
      .finally(() => setLoading(false));
  }, []);

  const submit = async () => {
    // The button is never dead: say what is missing instead of going grey.
    if (title.trim().length < 3) return setFormError('Give it a name — a few words is enough.');
    if (pitch.trim().length < 10) return setFormError('Add a sentence about how it would work.');
    setFormError(null);
    setBusy(true);
    setError(null);
    try {
      const res = await api2.pitchIdea({ title, pitch });
      setSent(res.message);
      setTitle('');
      setPitch('');
      setPitching(false);
      apply(await api2.ideas());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send that.');
    } finally {
      setBusy(false);
    }
  };

  const submitProposal = async () => {
    if (evTitle.trim().length < 3) return setFormError('Give the event a name.');
    if (!evDate) return setFormError('Pick a date, even a rough one.');
    if (evPitch.trim().length < 10) return setFormError('Add a sentence about what you have in mind.');
    setFormError(null);
    setBusy(true);
    setError(null);
    try {
      const res = await api2.pitchIdea({
        title: evTitle,
        pitch: evPitch,
        kind: 'event_proposal',
        eventDate: evDate,
        ...(evVenue.trim() ? { eventVenue: evVenue } : {}),
        ...(evCost.trim() ? { eventCost: evCost } : {}),
        ...(evFile ? { fileId: evFile.id } : {}),
      });
      setSent(res.message);
      setEvTitle('');
      setEvDate('');
      setEvVenue('');
      setEvCost('');
      setEvPitch('');
      setEvFile(null);
      setProposing(false);
      apply(await api2.ideas());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send that.');
    } finally {
      setBusy(false);
    }
  };

  const adopt = async (id: string) => {
    setBusy(true);
    try {
      apply(await api2.adoptIdea(id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not do that.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="muted">Loading…</p>;

  return (
    <div className="stack-lg">
      <header>
        <h1>Ideas</h1>
        <p className="soft" style={{ marginTop: 6 }}>
          Things that worked somewhere else, ready to borrow — and a place to put yours.
        </p>
      </header>

      {error && (
        <p className="notice alert" role="alert">
          {error}
        </p>
      )}
      {sent && <p className="notice mint">{sent}</p>}

      {!pitching && !proposing && (
        <div className="row">
          <button className="btn" onClick={() => setPitching(true)}>
            I have an idea
          </button>
          <button className="btn quiet" onClick={() => setProposing(true)}>
            Propose a booth or event
          </button>
        </div>
      )}

      {pitching && (
        <section className="card stack">
          <h2>What are you thinking?</h2>
          <div className="field">
            <label htmlFor="idea-title">Give it a name</label>
            <input
              id="idea-title"
              type="text"
              value={title}
              placeholder="Temple-festival flyer table"
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="idea-pitch">How would it work?</label>
            <AutoTextarea
              id="idea-pitch"
              value={pitch}
              placeholder="What it is, where, and what it would need."
              onChange={(e) => setPitch(e.target.value)}
              aria-describedby="idea-pitch-hint"
            />
            <span className="hint" id="idea-pitch-hint">
              Say a sentence about how it would work — a name alone isn't enough to go on.
            </span>
          </div>
          {formError && (
            <p className="notice alert" role="alert">
              {formError}
            </p>
          )}
          <div className="row">
            <button
              className="btn"
              onClick={() => void submit()}
              disabled={busy}
            >
              {busy ? 'Sending…' : 'Send it in'}
            </button>
            <button
              className="btn ghost"
              onClick={() => {
                setFormError(null);
                setPitching(false);
              }}
            >
              Back
            </button>
          </div>
        </section>
      )}

      {proposing && (
        <section className="card stack">
          <div>
            <h2>Propose a booth or event</h2>
            <p className="small muted">A real date and place makes it easy for your coordinator to say yes.</p>
          </div>
          <div className="field">
            <label htmlFor="ev-title">What's it called?</label>
            <input
              id="ev-title"
              type="text"
              value={evTitle}
              placeholder="Diwali mela booth"
              onChange={(e) => setEvTitle(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="ev-date">Proposed date</label>
            <input id="ev-date" type="date" value={evDate} onChange={(e) => setEvDate(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="ev-venue">Where?</label>
            <input
              id="ev-venue"
              type="text"
              value={evVenue}
              placeholder="Community centre, temple grounds…"
              onChange={(e) => setEvVenue(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="ev-cost">What would it cost?</label>
            <input
              id="ev-cost"
              type="text"
              value={evCost}
              placeholder="$50 table fee, or nothing"
              onChange={(e) => setEvCost(e.target.value)}
            />
            <span className="hint">A rough figure is fine.</span>
          </div>
          <div className="field">
            <label htmlFor="ev-pitch">What & why?</label>
            <AutoTextarea
              id="ev-pitch"
              value={evPitch}
              placeholder="Who comes, what we'd do there, and why it's worth it."
              onChange={(e) => setEvPitch(e.target.value)}
              aria-describedby="ev-pitch-hint"
            />
            {/* Same endpoint, same rule: the server wants at least a sentence. */}
            <span className="hint" id="ev-pitch-hint">
              Say a sentence about how it would work.
            </span>
          </div>
          <AttachField
            label="Attach your proposal (PDF or photo)"
            accept="image/*,application/pdf"
            value={evFile}
            onChange={setEvFile}
            hint="Optional — a flyer, a venue photo, a one-pager."
          />
          {formError && (
            <p className="notice alert" role="alert">
              {formError}
            </p>
          )}
          <div className="row">
            <button
              className="btn"
              onClick={() => void submitProposal()}
              disabled={busy}
            >
              {busy ? 'Sending…' : 'Send it in'}
            </button>
            <button
              className="btn ghost"
              onClick={() => {
                setFormError(null);
                setProposing(false);
              }}
            >
              Back
            </button>
          </div>
        </section>
      )}

      {mine.length > 0 && (
        <section className="stack">
          <h2>My ideas</h2>
          {mine.map((idea) => (
            <article
              key={idea.id}
              className="card stack"
              style={{ border: '2px dashed var(--marigold)', gap: 10 }}
            >
              <div className="row" style={{ gap: 8 }}>
                {idea.kind === 'event_proposal' && <span className="pill indigo">booth / event</span>}
                <span className={`pill ${idea.status === 'approved' ? 'mint' : 'marigold'}`}>
                  {idea.status === 'approved' ? 'Approved!' : idea.status === 'declined' ? 'Not this time' : 'Being looked at'}
                </span>
              </div>
              <div>
                <h2>{idea.title}</h2>
                {idea.kind === 'event_proposal' && (
                  <p className="small soft" style={{ marginTop: 6 }}>
                    {[
                      idea.eventDate ? formatDate(idea.eventDate) : null,
                      idea.eventVenue,
                      idea.eventCost,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                )}
                <p className="soft small" style={{ marginTop: 6 }}>
                  {idea.pitch}
                </p>
                {idea.attachment && (
                  <p className="small" style={{ marginTop: 6 }}>
                    <a href={`/api/files/${idea.attachment.id}`} target="_blank" rel="noopener">
                      {idea.attachment.name}
                    </a>
                  </p>
                )}
              </div>
              {idea.status === 'open' && (
                <p className="small muted">
                  Only you and the team can see this — it joins the shelf below once it's approved.
                </p>
              )}
            </article>
          ))}
        </section>
      )}

      <section className="stack">
        <h2>Ready to borrow</h2>
        {shared.length === 0 ? (
          <div className="card empty">
            <p>Nothing on the shelf yet. Yours could be the first.</p>
          </div>
        ) : (
          shared.map((idea) => (
            <article key={idea.id} className="card stack">
              <div className="row" style={{ gap: 8 }}>
                <span className={`pill ${idea.runningHere ? 'mint' : 'indigo'}`}>
                  {idea.runningHere
                    ? `Already running in ${cityName ?? 'your city'}`
                    : `Ready for ${cityName ?? 'your city'}`}
                </span>
                <span className="pill" style={{ marginLeft: 'auto' }}>
                  {idea.citiesAdopted > 0
                    ? `${idea.citiesAdopted} ${idea.citiesAdopted === 1 ? 'city' : 'cities'} adopted this`
                    : 'Not tried anywhere yet'}
                </span>
              </div>

              <div>
                <h2>{idea.title}</h2>
                <p className="soft" style={{ marginTop: 6 }}>
                  {idea.pitch}
                </p>
                {idea.cityName && <p className="small muted" style={{ marginTop: 6 }}>Piloted in {idea.cityName}.</p>}
              </div>

              {idea.notes.filter((n) => n.kind === 'learning').length > 0 && (
                <div className="stack" style={{ gap: 6 }}>
                  <span className="kicker">How it went</span>
                  {idea.notes
                    .filter((n) => n.kind === 'learning')
                    .map((note, i) => (
                      <p key={i} className="small soft">
                        {note.body}
                        {note.cityName ? ` — ${note.cityName}` : ''}
                      </p>
                    ))}
                </div>
              )}

              {/* A coordinator's offer to back it reads differently from notes
                  on how the pilot went — saffron, and louder. */}
              {idea.notes
                .filter((n) => n.kind === 'coordinator')
                .map((note, i) => (
                  <div key={i} className="notice marigold">
                    {/* Attributed to the city it came from. Labelling every
                        one "your coordinator" told a Denver volunteer that an
                        Austin coordinator's note was local authority. */}
                    <span className="kicker" style={{ display: 'block', marginBottom: 4 }}>
                      {note.cityName ? `${note.cityName} coordinator` : 'A coordinator'}
                    </span>
                    {note.body}
                  </div>
                ))}

              {!idea.runningHere && (
                <div>
                  <button className="btn" disabled={busy} onClick={() => void adopt(idea.id)}>
                    Lead this in {cityName ?? 'my city'}
                  </button>
                </div>
              )}
            </article>
          ))
        )}
      </section>
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
